import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { MessageIcon, PlusIcon } from '@repo/ui/icons'
import Modal from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API } from '../../../../api/apis.config'
import { createEnrollmentComment } from '../../../../api/enrollments/create-enrollment-comment'
import { updateEnrollmentComment } from '../../../../api/enrollments/update-enrollment-comment'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { EnrollmentComment } from '../../../../lib/types/models/enrollment-comment/enrollment-comment'
import { EnrollmentCommentInput } from '../../../../lib/types/models/enrollment-comment/enrollment-comment-input'
import { UpdateInput } from '../../../../lib/types/utils'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { DynamicTableRef } from '../../../elements/dynamic-table'
import NestedDataTable from '../../../elements/nested-table'
import EnrollmentCommentForm from './enrollment-comment-form'

export const enrollmentCommentsColumns: Column<EnrollmentComment>[] = [
  {
    accessorKey: 'comment',
    header: 'Commentaire',
    enableSorting: true,
    cell: ({ row }) => (
      <Typography
        variant="tag"
        color={tailwindColors.black.DEFAULT}
        className="text-wrap break-all px-3 py-4"
      >
        {row.original.comment}
      </Typography>
    )
  },
  {
    accessorKey: 'commentedBy',
    header: 'Commenté par',
    enableSorting: true,
    cell: ({ row }) =>
      toFullName(row.original.commentedBy.firstName, row.original.commentedBy.lastName)
  },
  {
    accessorKey: 'createdAt',
    enableSorting: true,
    header: 'Date & heure',
    cell: ({ row }) => (
      <Badge
        text={`${DateFormatter.DDMMYYYY(row.original.createdAt)} - ${DateFormatter.HHmm(row.original.createdAt)}`}
      />
    )
  }
]

type Props = {
  enrollmentId: number
}

const EnrollmentComments = ({ enrollmentId }: Props) => {
  const tableRef = useRef<DynamicTableRef<EnrollmentComment>>(null)
  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'edit',
    EnrollmentComment
  >(['create', 'edit'])

  const { mutateAsync: createEnrollmentCommentMutate } = useMutation({
    mutationFn: createEnrollmentComment,
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('create')
      toast.success('Commentaire créé avec succès')
    }
  })

  const { mutateAsync: updateEnrollmentCommentMutate } = useMutation({
    mutationFn: async (data: UpdateInput<EnrollmentCommentInput>) => {
      return updateEnrollmentComment(data, enrollmentId)
    },
    onError: showAxiosError,
    onSuccess: () => {
      tableRef.current?.refetch()
      closeModel('edit')
      toast.success('Commentaire modifié avec succès')
    }
  })

  return (
    <div id="enrollment-comments">
      <NestedDataTable<EnrollmentComment, null>
        ref={tableRef}
        overflow
        variant="action"
        title={'Commentaires'}
        deleteMessage={'Êtes-vous sûr de vouloir supprimer ce commentaire ?'}
        actions={[
          {
            icon: PlusIcon,
            variant: 'outline',
            label: 'Ajouter un commentaire',
            fn: () => toggleModal('create')
          }
        ]}
        columns={enrollmentCommentsColumns}
        onEditAction={(payment) => {
          toggleModal('edit', payment)
        }}
        endpoint={API.enrollments.getEnrollmentComments.url.replace(
          ':id',
          enrollmentId.toString()
        )}
      />

      <Modal
        isOpen={modalState.create}
        title={'Ajouter un commentaire'}
        icon={MessageIcon}
        hideActions
        size="medium"
        onCancel={() => closeModel('create')}
      >
        <EnrollmentCommentForm
          buttonsConfig={modalButtonConfig({
            onConfirm: async (values) => {
              await createEnrollmentCommentMutate({ id: enrollmentId, ...values })
            },
            onCancel: () => closeModel('create')
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          isOpen={modalState.edit}
          title={'Modifier un commentaire'}
          icon={MessageIcon}
          hideActions
          size="medium"
          onCancel={() => closeModel('edit')}
        >
          <EnrollmentCommentForm
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onConfirm: async (values) => {
                await updateEnrollmentCommentMutate({ id: selectedData.id, ...values })
              },
              onCancel: () => closeModel('edit')
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default EnrollmentComments
